/**
 * Bandeja de notificaciones del panel admin.
 */
import React, { useMemo, useState } from 'react';
import { ink, studio, SearchField, FilterDock, FilterPill, FilterMeta, DockActionRow } from './studioUi';

const MODULE_LABEL = {
    Reports: 'Reportes',
    Permissions: 'Accesos',
    Groups: 'Grupos',
};

const TYPE_LABEL = {
    permission_revoked: 'Acceso revocado',
    report_access: 'Acceso asignado',
    permission_update: 'Acceso actualizado',
    group_membership: 'Nuevo grupo',
    group_membership_revoked: 'Saliste de un grupo',
    group_permission_added: 'Acceso de grupo',
    report_approval_complete: 'Aprobado',
    report_approval_reverted: 'Volvió a pendiente',
    report_responsible_assigned: 'Eres responsable',
    report_responsible_approval_changed: 'Cambio de aprobación',
    report_responsible_removed: 'Ya no eres responsable',
    report_deadline_warning: 'Deadline cercano',
    report_deadline_overdue: 'Deadline vencido',
    report_deadline_updated: 'Deadline actualizado',
    user_mention_in_comment: 'Te mencionaron',
    new_comment_on_report: 'Comentario',
    adjustment_created: 'Nuevo ajuste',
    adjustment_reassigned: 'Ajuste asignado',
    adjustment_completed: 'Ajuste completado',
    adjustment_validated: 'Ajuste validado',
    adjustment_reopened: 'Ajuste reabierto',
};

const TYPE_TONE = {
    report_deadline_overdue: { backgroundColor: ink.dangerSoft, color: ink.danger },
    report_deadline_warning: { backgroundColor: '#fff3e0', color: '#e65100' },
    report_approval_complete: { backgroundColor: '#e6f4ea', color: '#137333' },
    permission_revoked: { backgroundColor: ink.dangerSoft, color: ink.danger },
    group_membership_revoked: { backgroundColor: ink.dangerSoft, color: ink.danger },
    user_mention_in_comment: { backgroundColor: ink.accentSoft, color: ink.accentText },
};

const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp._seconds !== undefined) return new Date(timestamp._seconds * 1000);
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatWhen = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return 'Sin fecha';
    const diff = Date.now() - date.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `Hace ${days} d`;
    return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const isNavigable = (notification) => {
    if (!notification?.entityId) return false;
    return notification.module === 'Reports'
        || notification.module === 'Permissions'
        || notification.module === 'Groups'
        || notification.type === 'user_mention_in_comment'
        || notification.type === 'new_comment_on_report';
};

const NotificationPanel = ({ notifications = [], onMarkAsRead, onLinkClick, onMarkAllAsRead }) => {
    const [query, setQuery] = useState('');
    const [scope, setScope] = useState('all');
    const [moduleFilter, setModuleFilter] = useState('');
    const [hoveredId, setHoveredId] = useState(null);

    const unreadCount = notifications.filter((item) => !item.read).length;
    const modules = useMemo(
        () => [...new Set(notifications.map((item) => item.module).filter(Boolean))].sort(),
        [notifications]
    );

    const visible = useMemo(() => {
        const term = query.trim().toLowerCase();
        return notifications
            .filter((item) => {
                if (scope === 'unread' && item.read) return false;
                if (scope === 'read' && !item.read) return false;
                if (moduleFilter && item.module !== moduleFilter) return false;
                if (!term) return true;
                const typeLabel = TYPE_LABEL[item.type] || item.type || '';
                const moduleLabel = MODULE_LABEL[item.module] || item.module || '';
                return (item.message || '').toLowerCase().includes(term)
                    || typeLabel.toLowerCase().includes(term)
                    || moduleLabel.toLowerCase().includes(term);
            })
            .sort((a, b) => {
                if (!!a.read !== !!b.read) return a.read ? 1 : -1;
                return (toDate(b.timestamp)?.getTime() || 0) - (toDate(a.timestamp)?.getTime() || 0);
            });
    }, [notifications, query, scope, moduleFilter]);

    const openNotification = (notification) => {
        if (!isNavigable(notification)) return;
        if (!notification.read) onMarkAsRead(notification.id);
        onLinkClick(notification);
    };

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Bandeja</p>
                    <h2 style={studio.title}>Notificaciones</h2>
                    <p style={studio.subtitle}>
                        {unreadCount === 0
                            ? 'No tienes avisos pendientes.'
                            : unreadCount === 1
                                ? 'Tienes 1 aviso sin leer.'
                                : `Tienes ${unreadCount} avisos sin leer.`}
                    </p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{notifications.length}</span>
                        <span style={studio.statLabel}>Total</span>
                    </div>
                    <div style={{ ...studio.stat, borderLeftColor: unreadCount ? ink.accent : ink.line }}>
                        <span style={studio.statValue}>{unreadCount}</span>
                        <span style={studio.statLabel}>Sin leer</span>
                    </div>
                </div>
            </div>

            <div style={studio.card}>
                <FilterDock>
                    <DockActionRow
                        action={unreadCount > 0 ? (
                            <button type="button" onClick={onMarkAllAsRead} style={studio.ghostBtn}>
                                Marcar todo como leído
                            </button>
                        ) : null}
                    >
                        <SearchField
                            value={query}
                            onChange={setQuery}
                            placeholder="Buscar aviso…"
                            style={{ marginBottom: 0 }}
                        />
                    </DockActionRow>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={studio.scopeBar}>
                            {[
                                { id: 'all', label: 'Todas' },
                                { id: 'unread', label: 'Sin leer' },
                                { id: 'read', label: 'Leídas' },
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setScope(item.id)}
                                    style={scope === item.id ? studio.scopeOn : studio.scopeOff}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        {modules.length > 0 && (
                            <FilterPill value={moduleFilter} onChange={setModuleFilter} emptyLabel="Módulo">
                                {modules.map((module) => (
                                    <option key={module} value={module}>{MODULE_LABEL[module] || module}</option>
                                ))}
                            </FilterPill>
                        )}
                    </div>
                    <FilterMeta
                        summary={`${visible.length} ${visible.length === 1 ? 'aviso' : 'avisos'}`}
                        chips={[
                            query && { key: 'q', label: `“${query}”`, onClear: () => setQuery('') },
                            scope !== 'all' && { key: 'scope', label: scope === 'unread' ? 'Sin leer' : 'Leídas', onClear: () => setScope('all') },
                            moduleFilter && { key: 'mod', label: MODULE_LABEL[moduleFilter] || moduleFilter, onClear: () => setModuleFilter('') },
                        ].filter(Boolean)}
                        onClearAll={() => { setQuery(''); setScope('all'); setModuleFilter(''); }}
                    />
                </FilterDock>

                {visible.length === 0 ? (
                    <div style={studio.empty}>
                        {notifications.length === 0 ? 'No tienes notificaciones.' : 'Ningún aviso coincide con los filtros.'}
                    </div>
                ) : (
                    <div style={ui.list}>
                        {visible.map((notification) => {
                            const unread = !notification.read;
                            const canOpen = isNavigable(notification);
                            const hot = hoveredId === notification.id;
                            const typeLabel = TYPE_LABEL[notification.type] || notification.type || 'Aviso';
                            const typeTone = TYPE_TONE[notification.type] || { backgroundColor: ink.wash, color: ink.muted };
                            return (
                                <article
                                    key={notification.id}
                                    style={{
                                        ...ui.row,
                                        ...(unread ? ui.rowUnread : {}),
                                        ...(hot ? ui.rowHot : {}),
                                        cursor: canOpen ? 'pointer' : 'default',
                                    }}
                                    onMouseEnter={() => setHoveredId(notification.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    onClick={() => openNotification(notification)}
                                >
                                    {unread && <span style={ui.dot} />}
                                    <div style={ui.body}>
                                        <p style={{ ...ui.message, color: unread ? ink.text : ink.muted }}>
                                            {notification.message}
                                        </p>
                                        <div style={ui.meta}>
                                            <span style={{ ...ui.chip, ...typeTone }}>{typeLabel}</span>
                                            <span style={ui.chipMuted}>{MODULE_LABEL[notification.module] || notification.module || 'General'}</span>
                                            <span style={ui.when}>{formatWhen(notification.timestamp)}</span>
                                        </div>
                                    </div>
                                    <div style={ui.actions} onClick={(e) => e.stopPropagation()}>
                                        {canOpen && (
                                            <button
                                                type="button"
                                                style={ui.compactGhost}
                                                onClick={() => openNotification(notification)}
                                            >
                                                Abrir
                                            </button>
                                        )}
                                        {unread && (
                                            <button
                                                type="button"
                                                style={ui.compactPrimary}
                                                onClick={() => onMarkAsRead(notification.id)}
                                            >
                                                Marcar leído
                                            </button>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const ui = {
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxHeight: '68vh',
        overflowY: 'auto',
        paddingRight: 4,
    },
    row: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px 14px 22px',
        borderRadius: 14,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.surface,
    },
    rowUnread: {
        backgroundColor: '#fbf8ff',
        borderColor: '#e4d7f5',
    },
    rowHot: {
        boxShadow: '0 6px 16px rgba(28, 29, 33, 0.06)',
    },
    dot: {
        position: 'absolute',
        left: 9,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: ink.accent,
    },
    body: { flex: 1, minWidth: 0 },
    message: {
        margin: 0,
        fontSize: '0.95rem',
        fontWeight: 600,
        letterSpacing: '-0.015em',
        lineHeight: 1.4,
    },
    meta: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
    },
    chip: {
        borderRadius: 999,
        padding: '3px 9px',
        fontSize: '0.72rem',
        fontWeight: 700,
    },
    chipMuted: {
        borderRadius: 999,
        padding: '3px 9px',
        fontSize: '0.72rem',
        fontWeight: 600,
        backgroundColor: ink.wash,
        color: ink.faint,
    },
    when: {
        fontSize: '0.78rem',
        color: ink.faint,
        fontWeight: 600,
    },
    actions: {
        display: 'flex',
        gap: 8,
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    compactGhost: {
        ...studio.ghostBtn,
        padding: '8px 12px',
        fontSize: '0.82rem',
    },
    compactPrimary: {
        ...studio.primaryBtn,
        padding: '8px 12px',
        fontSize: '0.82rem',
    },
};

export default NotificationPanel;
